import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  FetchProcessoArbitralRascunhosParams,
  InitializeProcessoArbitralDraftInput,
  ProcessoArbitralDraftDetail,
  ProcessoArbitralDraftListResult,
  ProcessoArbitralDraftSnapshot,
  ProcessoArbitralDraftStep,
} from '@/features/processo-arbitral/types/rascunhoStepTypes';
import {
  createEmptyProcessoArbitralDraftSnapshot,
  normalizeProcessoArbitralDraftSnapshot,
} from '@/features/processo-arbitral/utils/rascunhoStepUtils';
import { getErrorMessage } from '@/shared/utils/error';
import { apiGet } from '@/shared/utils/requestBuilder';
import { getStoredWorkspaceId, setStoredWorkspaceId } from '@/shared/utils/workspace';
import type { RootState } from '@/store';

import { ProcessoArbitralDraftRequestError, processoArbitralRascunhoStepService } from './services';

export const RASCUNHO_ARBITRAL_INDISPONIVEL = 'Este rascunho não existe mais';

const STATUS_DE_RASCUNHO_INDISPONIVEL = [403, 404, 410];

const ehRascunhoIndisponivel = (error: unknown): boolean =>
  error instanceof ProcessoArbitralDraftRequestError &&
  STATUS_DE_RASCUNHO_INDISPONIVEL.includes(error.status);

interface WorkspaceAuthData {
  escritorioId?: string | null;
  lastWorkspaceId?: string | null;
}

const normalizeDraftDetail = (draft: ProcessoArbitralDraftDetail): ProcessoArbitralDraftDetail => ({
  ...draft,
  payload: normalizeProcessoArbitralDraftSnapshot(draft.payload),
});

const extractWorkspaceAuthData = (value: unknown): WorkspaceAuthData | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const source = 'data' in value ? (value as { data?: unknown }).data : value;

  if (!source || typeof source !== 'object') {
    return null;
  }

  const authData = source as WorkspaceAuthData;
  return authData;
};

const ensureWorkspaceId = async (): Promise<string> => {
  if (typeof window === 'undefined') {
    throw new Error('Workspace n�o definido');
  }

  const storedWorkspaceId = getStoredWorkspaceId()?.trim();
  if (storedWorkspaceId) {
    return storedWorkspaceId;
  }

  const authResponse = await apiGet('/auth/me');
  const authJson = (await authResponse.json().catch(() => null)) as unknown;
  const authData = authResponse.ok ? extractWorkspaceAuthData(authJson) : null;
  const fallbackWorkspaceId = authData?.lastWorkspaceId?.trim() || authData?.escritorioId?.trim();

  if (fallbackWorkspaceId) {
    setStoredWorkspaceId(fallbackWorkspaceId);
    return fallbackWorkspaceId;
  }

  const response = await apiGet('/escritorios/pessoal');
  const json = (await response.json().catch(() => null)) as unknown;

  if (!response.ok || typeof json !== 'object' || json === null) {
    throw new Error('Workspace n�o definido');
  }

  const payload =
    'data' in json && typeof (json as { data?: unknown }).data === 'object'
      ? ((json as { data?: { id?: unknown } }).data ?? null)
      : json;

  const workspaceId =
    payload && typeof payload === 'object' && typeof (payload as { id?: unknown }).id === 'string'
      ? (payload as { id: string }).id.trim()
      : '';

  if (!workspaceId) {
    throw new Error('Workspace n�o definido');
  }

  setStoredWorkspaceId(workspaceId);
  return workspaceId;
};

export const fetchProcessoArbitralRascunhos = createAsyncThunk<
  ProcessoArbitralDraftListResult,
  FetchProcessoArbitralRascunhosParams | undefined,
  { rejectValue: string }
>(
  'processoArbitral/rascunhoStep/fetchProcessoArbitralRascunhos',
  async (params, { rejectWithValue }) => {
    try {
      const escritorioId = await ensureWorkspaceId();
      const resposta = await processoArbitralRascunhoStepService.list(params);

      return { ...resposta, escritorioId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const initializeProcessoArbitralDraft = createAsyncThunk<
  ProcessoArbitralDraftDetail,
  InitializeProcessoArbitralDraftInput,
  { rejectValue: string }
>(
  'processoArbitral/rascunhoStep/initializeProcessoArbitralDraft',
  async (input, { rejectWithValue }) => {
    try {
      await ensureWorkspaceId();
      let draft: ProcessoArbitralDraftDetail | null = null;

      if (input.draftId) {
        draft = await processoArbitralRascunhoStepService.getById(input.draftId);
      }

      if (!draft && input.fallbackToLatest) {
        draft = await processoArbitralRascunhoStepService.getLatest();
      }

      if (!draft && input.createIfMissing) {
        draft = await processoArbitralRascunhoStepService.createDraft({
          ultimoPasso: input.ultimoPasso,
          snapshot: createEmptyProcessoArbitralDraftSnapshot(),
        });
      }

      if (!draft) {
        return rejectWithValue(RASCUNHO_ARBITRAL_INDISPONIVEL);
      }

      return normalizeDraftDetail(draft);
    } catch (error) {
      if (ehRascunhoIndisponivel(error)) {
        return rejectWithValue(RASCUNHO_ARBITRAL_INDISPONIVEL);
      }

      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const saveProcessoArbitralDraftSnapshot = createAsyncThunk<
  { draft: ProcessoArbitralDraftDetail; criado: boolean },
  { snapshot: ProcessoArbitralDraftSnapshot; ultimoPasso?: ProcessoArbitralDraftStep },
  { rejectValue: string; state: RootState }
>(
  'processoArbitral/rascunhoStep/saveProcessoArbitralDraftSnapshot',
  async ({ snapshot, ultimoPasso }, { getState, rejectWithValue }) => {
    try {
      await ensureWorkspaceId();
      const state = getState();
      const draftId = state.processoArbitralRascunhoStep.activeDraft?.id;
      const normalizedSnapshot = normalizeProcessoArbitralDraftSnapshot(snapshot);

      const draft = draftId
        ? await processoArbitralRascunhoStepService.updateDraft(draftId, {
            ultimoPasso,
            snapshot: normalizedSnapshot,
          })
        : await processoArbitralRascunhoStepService.createDraft({
            ultimoPasso,
            snapshot: normalizedSnapshot,
          });

      return { draft: normalizeDraftDetail(draft), criado: !draftId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const discardProcessoArbitralDraft = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'processoArbitral/rascunhoStep/discardProcessoArbitralDraft',
  async (draftId, { rejectWithValue }) => {
    try {
      await ensureWorkspaceId();
      await processoArbitralRascunhoStepService.discard(draftId);
      return draftId;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
